import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PageHeader from "../../component/pageHeaders";
import { Button, message, Select } from "antd";
import { IoReloadOutline } from "react-icons/io5";
import PodcastForm from "./component/podcastForm";
import axios from "../../config/axiosInstance";
import DataTable from "./component/datatable";
import SearchInput from "../../component/SearchInput";
import "./styles.css";
import { API } from "../../config/API";

const { Option } = Select;

function PodcastScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const [isTable, setIsTable] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<number>(0);
  const [item, setItem] = useState({});
  const [searchTerm, setSearchTerm] = useState(query.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(query.get("status") || "");
  const [page, setPage] = useState(
    query.get("page") ? parseInt(query.get("page")!) : 1,
  );
  const [pageSize, setPageSize] = useState(
    query.get("limit") ? parseInt(query.get("limit")!) : 10,
  );

  useEffect(() => {
    const currentQuery = new URLSearchParams(location.search);
    const currentPage = currentQuery.get("page")
      ? parseInt(currentQuery.get("page")!)
      : 1;
    const currentPageSize = currentQuery.get("limit")
      ? parseInt(currentQuery.get("limit")!)
      : 10;
    const currentSearch = currentQuery.get("search") || "";
    const currentStatus = currentQuery.get("status") || "";
    setPage(currentPage);
    setPageSize(currentPageSize);
    setSearchTerm(currentSearch);
    setStatusFilter(currentStatus);
    loadDataWithParams(
      currentPage,
      currentPageSize,
      currentSearch,
      currentStatus,
    );
  }, [location.search]);

  const loadDataWithParams = (
    currentPage: number,
    currentPageSize: number,
    search: string = "",
    status: string = "",
  ) => {
    setIsLoading(true);
    let filters = `?populate=*&publicationState=preview&sort=createdAt:DESC`;
    filters += `&pagination[page]=${currentPage}&pagination[pageSize]=${currentPageSize}`;
    if (search) {
      filters += `&filters[title][$containsi]=${encodeURIComponent(search)}`;
    }
    if (status === "published") {
      filters += `&filters[publishedAt][$notNull]=true`;
    } else if (status === "draft") {
      filters += `&filters[publishedAt][$null]=true`;
    }
    const url = API.BASE_URL + API.PODCAST + filters;
    axios
      .get(url)
      .then((response) => {
        setData(response.data.data || []);
        const total = response.data.meta?.pagination?.total || 0;
        setMeta(total);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
        message.error("Failed to load podcasts");
      });
  };

  const loadData = () => {
    loadDataWithParams(page, pageSize, searchTerm, statusFilter);
  };

  const onPageChange = (newPage: number, newPageSize: number) => {
    const currentQuery = new URLSearchParams(location.search);
    currentQuery.set("page", newPage.toString());
    currentQuery.set("limit", newPageSize.toString());
    navigate({ search: currentQuery.toString() });
    window.scrollTo(0, 0);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    const currentQuery = new URLSearchParams(location.search);
    currentQuery.set("page", "1");
    if (value) {
      currentQuery.set("search", value);
    } else {
      currentQuery.delete("search");
    }
    navigate({ search: currentQuery.toString() });
  };

  const handleStatusFilter = (value: string) => {
    const filterValue = value === "all" ? "" : value;
    setStatusFilter(filterValue);
    const currentQuery = new URLSearchParams(location.search);
    currentQuery.set("page", "1");
    if (filterValue) {
      currentQuery.set("status", filterValue);
    } else {
      currentQuery.delete("status");
    }
    navigate({ search: currentQuery.toString() });
  };

  const deleteData = (id: any) => {
    const url = API.BASE_URL + API.PODCAST + `/${id}`;
    axios
      .delete(url)
      .then(() => {
        message.success("Successfully deleted");
        const remaining = data.filter((item: any) => item.id !== id);
        setData(remaining as any);
        setMeta((prev) => Math.max(0, prev - 1));
        if (remaining.length === 0 && page > 1) {
          const currentQuery = new URLSearchParams(location.search);
          currentQuery.set("page", (page - 1).toString());
          navigate({ search: currentQuery.toString() });
        }
      })
      .catch(() => {
        message.error("Something went wrong!");
      });
  };

  const onEdit = (val: any) => {
    setItem(val);
    setIsTable(false);
  };

  const handleTogglePublish = async (
    record: any,
    checked: boolean,
    onComplete: () => void,
  ) => {
    // Optimistically update local state
    const newPublishedAt = checked ? new Date().toISOString() : null;
    setData((prevData: any) =>
      prevData.map((item: any) =>
        item.id === record.id
          ? {
              ...item,
              attributes: { ...item.attributes, publishedAt: newPublishedAt },
            }
          : item,
      ),
    );

    try {
      const url = API.BASE_URL + API.PODCAST + `/${record.id}`;
      const requestObj = {
        data: {
          publishedAt: checked ? new Date() : null,
        },
      };

      const response = await axios.put(url, requestObj);

      if (response.data?.data) {
        message.success(
          checked
            ? "Podcast published successfully"
            : "Podcast unpublished successfully",
        );
        // Reload to reflect correct data with current filters
        loadData();
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error: any) {
      console.error("Error toggling publish status:", error);
      message.error(
        error?.response?.data?.error?.message ||
          "Failed to update publish status. Please try again.",
      );
      loadData();
    } finally {
      onComplete();
    }
  };

  return (
    <div style={{ height: "100vh", overflow: "auto" }}>
      {isTable ? (
        <PageHeader title={"Podcast"} length={`${meta || 0} Podcasts`}>
          <div
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Button
              icon={<IoReloadOutline />}
              onClick={loadData}
              loading={isLoading}
            >
              Refresh
            </Button>
            <SearchInput
              onSearch={handleSearch}
              placeholder="Search podcasts..."
              defaultValue={searchTerm}
            />
            <Select
              placeholder="Filter by status"
              style={{ width: 150 }}
              value={statusFilter || undefined}
              onChange={handleStatusFilter}
              allowClear
              onClear={() => handleStatusFilter("all")}
            >
              <Option value="published">Published</Option>
              <Option value="draft">Draft</Option>
            </Select>
            <Button
              type="primary"
              onClick={() => {
                setItem({});
                setIsTable(false);
              }}
            >
              Add New +
            </Button>
          </div>
        </PageHeader>
      ) : null}
      <div>
        {!isTable ? (
          <PodcastForm
            items={item}
            onClose={() => {
              setIsTable(true);
              loadData();
            }}
          />
        ) : (
          <DataTable
            data={data}
            meta={meta}
            page={page}
            pagesize={pageSize}
            loading={isLoading}
            onDelete={(id: any) => deleteData(id)}
            onEdit={(val: any) => onEdit(val)}
            onTogglePublish={handleTogglePublish}
            onPageChange={(p: number, ps: number) => onPageChange(p, ps)}
          />
        )}
      </div>
    </div>
  );
}

export default PodcastScreen;
